import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { User, Loader2, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useClientAuth } from "@/context/ClientAuthContext";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Input } from "@/components/ui/input";
import { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function ClientLogin() {
  const { user, login, register } = useClientAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate("/account", { replace: true }); }, [user, navigate]);
  useEffect(() => { if (params.get("error") === "google") toast.error(t("portal.google_fail")); }, [params, t]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "register") await register(name, email, password);
      else await login(email, password);
      navigate("/account", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || t("portal.login_fail"));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inputCls = "bg-[#050505] border-white/10 rounded-none h-12 text-white placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0";

  return (
    <div className="noise-overlay bg-[#050505] min-h-screen flex items-center justify-center px-5 grid-bg" data-testid="client-login-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><button onClick={() => navigate("/")}><Logo height="h-11" /></button></div>
        <div className="flex justify-center items-center gap-3 mb-8"><LanguageSwitcher /><ThemeToggle /></div>
        <div className="border border-white/10 bg-[#0C0C0E] p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 flex items-center justify-center border border-[#00E5FF]/40 bg-[#00E5FF]/10">
              <User className="w-4 h-4 text-[#00E5FF]" />
            </div>
            <div>
              <h1 className="font-display font-900 text-white text-xl tracking-tight uppercase">{t("portal.login_title")}</h1>
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t("portal.login_sub")}</p>
            </div>
          </div>

          <div className="flex mb-7 border border-white/10">
            <button onClick={() => setMode("login")} data-testid="client-tab-login" className={`flex-1 py-2.5 font-mono-tech text-[11px] uppercase tracking-[0.15em] transition-colors ${mode === "login" ? "bg-[#E60000] text-white" : "text-zinc-500 hover:text-white"}`}>{t("portal.tab_login")}</button>
            <button onClick={() => setMode("register")} data-testid="client-tab-register" className={`flex-1 py-2.5 font-mono-tech text-[11px] uppercase tracking-[0.15em] transition-colors ${mode === "register" ? "bg-[#E60000] text-white" : "text-zinc-500 hover:text-white"}`}>{t("portal.tab_register")}</button>
          </div>

          <button onClick={googleLogin} data-testid="client-google-btn" className="w-full inline-flex items-center justify-center gap-3 h-12 bg-white text-[#0C0C0E] font-display font-700 hover:bg-zinc-200 transition-colors mb-6">
            <svg className="w-5 h-5" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            {t("portal.google")}
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-600">{t("portal.or")}</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-5">
            {mode === "register" && (
              <div>
                <label className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 block mb-2">{t("portal.name")}</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="client-name" placeholder="" className={inputCls} required />
              </div>
            )}
            <div>
              <label className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 block mb-2">{t("portal.email")}</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="client-email" placeholder="" className={inputCls} required />
            </div>
            <div>
              <label className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 block mb-2">{t("portal.password")}</label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="client-password" placeholder="••••••••" className={inputCls} required minLength={6} />
            </div>
            {error && <p className="text-[#FF1A1A] text-sm font-mono-tech" data-testid="client-login-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="client-login-submit" className="w-full inline-flex items-center justify-center gap-2 h-12 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogIn className="w-4 h-4" /> {mode === "register" ? t("portal.signup") : t("portal.signin")}</>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
